import { AbortControllerManager } from '../abortControllerManager';
import { authClient } from '../client';
import { ITemplateResponse } from '@/types/common';
import {
  TFormBuilderEditTemplate,
  TFormBuilderEntry,
  TPatchFormBuilderEntryRequest,
  TPatchFormBuilderEntrySignatureRequest,
} from '@/types/formBuilder';

class FormBuilderApi extends AbortControllerManager {
  private FORMS_URL = '/v2/forms';

  constructor() {
    super();
  }

  getFormTemplates(
    institutionId: string,
  ): Promise<ITemplateResponse<TFormBuilderEditTemplate[]>> {
    const params = new URLSearchParams({ institution_id: institutionId });
    const signal = this.createAbortController('getFormTemplates');
    return authClient.get(`${this.FORMS_URL}?${params}`, { signal });
  }

  createFormTemplate(
    body: Omit<TFormBuilderEditTemplate, 'id'>,
  ): Promise<ITemplateResponse<TFormBuilderEditTemplate>> {
    const signal = this.createAbortController('createFormTemplate');
    return authClient.post(this.FORMS_URL, body, { signal });
  }

  updateFormTemplate({
    id,
    ...body
  }: TFormBuilderEditTemplate): Promise<
    ITemplateResponse<TFormBuilderEditTemplate>
  > {
    const signal = this.createAbortController('updateFormTemplate');
    return authClient.put(`${this.FORMS_URL}/${id}`, body, { signal });
  }

  deleteFormTemplate(id: string): Promise<void> {
    const signal = this.createAbortController('deleteFormTemplate');
    return authClient.delete(`${this.FORMS_URL}/${id}`, { signal });
  }

  createFormEntry(
    body: Partial<TFormBuilderEntry>,
  ): Promise<ITemplateResponse<TFormBuilderEntry>> {
    const signal = this.createAbortController('createFormEntry');
    return authClient.post(`${this.FORMS_URL}/entries`, body, { signal });
  }

  patchFormEntry(
    id: string,
    body: TPatchFormBuilderEntryRequest,
  ): Promise<ITemplateResponse<TFormBuilderEntry>> {
    const signal = this.createAbortController(`patchFormEntry-${id}`);
    return authClient.patch(`${this.FORMS_URL}/entries/${id}`, body, {
      signal,
    });
  }

  patchFormEntrySignature(
    id: string,
    body?: TPatchFormBuilderEntrySignatureRequest,
  ): Promise<ITemplateResponse<TFormBuilderEntry>> {
    const signal = this.createAbortController(`patchFormEntrySignature-${id}`);
    const url = `${this.FORMS_URL}/entries/${id}/signature`;

    if (!body) return authClient.post(url, undefined, { signal });

    const formData = new FormData();
    Object.entries(body).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });

    return authClient.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      signal,
    });
  }

  deleteFormEntry(id: string): Promise<void> {
    const signal = this.createAbortController('deleteFormEntry');
    return authClient.delete(`${this.FORMS_URL}/entries/${id}`, { signal });
  }

  signFormEntry(
    id: string,
    body: { signature: string },
  ): Promise<ITemplateResponse<TFormBuilderEntry>> {
    const signal = this.createAbortController('signFormEntry');
    return authClient.post(`${this.FORMS_URL}/entries/${id}/signature`, body, {
      signal,
    });
  }
}

export const formBuilderApi = new FormBuilderApi();
